import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { UiButtonComponent } from '../../../../components/button/button.component';
import { SettingService } from '../../../../services/setting/setting.service';
import { AppService } from '../../../../core/app.service';
import { ParametreRequest } from '../../../../data/http/requests/setting.req';
import { ThemeService, BrandColorKey } from '../../../../services/setting/theme.service';

const TYPE_SYSTEM_CONFIG = 'SYSTEM_CONFIG';
const CODE_COLOR_THEME = 'COLOR_THEME';
const CODE_COLOR_THEME_DEFAULT = 'COLOR_THEME_DEFAULT';

interface ColorFieldDef {
  key: BrandColorKey;
  label: string;
  hint: string;
}

const COLOR_FIELDS: ColorFieldDef[] = [
  { key: 'colorPrimary', label: 'Couleur primaire', hint: 'Couleur principale de l\'organisation' },
  { key: 'colorAccentVariant', label: 'Couleur secondaire', hint: 'Boutons principaux dans toute l\'app' },
  { key: 'colorAccentBase', label: 'Couleur d\'accentuation', hint: 'Sidebar, étapes, sélections actives' },
];

@Component({
  selector: 'app-couleur',
  standalone: true,
  imports: [ReactiveFormsModule, UiButtonComponent],
  templateUrl: './couleur.component.html',
  styleUrl: './couleur.component.scss'
})
export class CouleurComponent implements OnInit {
  private fb = inject(FormBuilder);
  private settingService = inject(SettingService);
  private appService = inject(AppService);
  private themeService = inject(ThemeService);

  readonly fields = COLOR_FIELDS;

  form: FormGroup = this.fb.group(
    Object.fromEntries(COLOR_FIELDS.map(f => [f.key, [this.themeService.getBrandColors()[f.key]]]))
  );

  loading = false;
  saving = false;
  savingDefault = false;
  restoring = false;

  private parametreId: string | null = null;
  private colorThemeDetailId: string | undefined;
  private colorThemeDefaultDetailId: string | undefined;
  private activeColors: Partial<Record<BrandColorKey, string>> | null = null;

  /** Couleurs par défaut organisation, gardées en mémoire pour "Restaurer" sans refaire un GET */
  private defaultColors: Partial<Record<BrandColorKey, string>> | null = null;

  ngOnInit(): void {
    this.loading = true;
    const appId = this.appService.getAppId();
    this.settingService.getByAppIdAndType(appId, TYPE_SYSTEM_CONFIG).subscribe({
      next: (res) => {
        const data = res.data;
        if (data) {
          this.parametreId = data.id;
          const details = data.listeDetailsParametres ?? [];

          const activeDetail = details.find(d => d.code === CODE_COLOR_THEME);
          if (activeDetail) {
            this.colorThemeDetailId = activeDetail.id;
            const parsed = this.safeParse(activeDetail.valeurJson);
            if (parsed) {
              this.form.patchValue(parsed);
              this.themeService.applyBrandColors(parsed);
              this.activeColors = parsed;
            }
          }

          const defaultDetail = details.find(d => d.code === CODE_COLOR_THEME_DEFAULT);
          if (defaultDetail) {
            this.colorThemeDefaultDetailId = defaultDetail.id;
            this.defaultColors = this.safeParse(defaultDetail.valeurJson);
            
          }
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('Échec chargement config système', err);
        this.loading = false;
      }
    });
  }

  private safeParse(json: string | undefined): Partial<Record<BrandColorKey, string>> | null {
    if (!json) return null;
    try {
      return JSON.parse(json);
    } catch (e) {
      console.error('valeurJson invalide', e);
      return null;
    }
  }

  /** Preview live à chaque changement, avant toute sauvegarde */
  onColorInput(key: BrandColorKey, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.form.get(key)?.setValue(value);
    this.themeService.setBrandColor(key, value);
  }

  /** Action 1 — Enregistrer : sauvegarde la sélection courante en COLOR_THEME (actif) uniquement */
  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    const appId = this.appService.getAppId();
    const colorsValue: Partial<Record<BrandColorKey, string>> = this.form.value;

    const request: ParametreRequest = {
      libelle: 'Configuration système',
      typeParametreCode: TYPE_SYSTEM_CONFIG,
      applicationId: appId,
      listeDetailsParametres: [
        {
          id: this.colorThemeDetailId,
          code: CODE_COLOR_THEME,
          libelle: 'Thème de couleurs (actif)',
          valeurJson: JSON.stringify(colorsValue),
        },
      ],
    };

    const call = this.parametreId
      ? this.settingService.update(this.parametreId, request)
      : this.settingService.create(request);

    call.subscribe({
      next: (res) => {
        this.saving = false;
        this.syncDetailIds(res.data);
        this.themeService.applyBrandColors(colorsValue);
        this.activeColors = colorsValue;
        this.settingService.refreshBrand(appId);
      },
      error: () => { this.saving = false; }
    });
  }

  onCancel(): void {
    const fallback = this.activeColors ?? this.themeService.getBrandColors();
    this.form.patchValue(fallback);
    this.themeService.applyBrandColors(fallback);
  }

  /** Action 2 — Définir comme défaut : copie la sélection courante dans COLOR_THEME_DEFAULT */
  onSetAsDefault(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.savingDefault = true;
    const appId = this.appService.getAppId();
    const colorsValue: Partial<Record<BrandColorKey, string>> = this.form.value;

    const request: ParametreRequest = {
      libelle: 'Configuration système',
      typeParametreCode: TYPE_SYSTEM_CONFIG,
      applicationId: appId,
      listeDetailsParametres: [
        {
          id: this.colorThemeDefaultDetailId,
          code: CODE_COLOR_THEME_DEFAULT,
          libelle: 'Thème de couleurs (défaut organisation)',
          valeurJson: JSON.stringify(colorsValue),
        },
      ],
    };

    const call = this.parametreId
      ? this.settingService.update(this.parametreId, request)
      : this.settingService.create(request);

    call.subscribe({
      next: (res) => {
        this.savingDefault = false;
        this.syncDetailIds(res.data);
        this.defaultColors = colorsValue;
      },
      error: () => { this.savingDefault = false; }
    });
  }

  /** Action 3 — Restaurer le défaut : recharge COLOR_THEME_DEFAULT dans le formulaire + preview.
   *  Ne sauvegarde PAS automatiquement en actif — l'admin doit cliquer "Enregistrer" pour confirmer. */
  onRestoreDefault(): void {
    if (!this.defaultColors) {
      return; // aucun défaut organisation encore défini
    }
    this.restoring = true;
    this.form.patchValue(this.defaultColors);
    this.themeService.applyBrandColors(this.defaultColors);
    this.restoring = false;
  }

  get hasDefault(): boolean {
    return !!this.defaultColors;
  }

  private syncDetailIds(data: any): void {
    if (!data) return;
    this.parametreId = data.id;
    const details = data.listeDetailsParametres ?? [];
    const active = details.find((d: any) => d.code === CODE_COLOR_THEME);
    if (active) this.colorThemeDetailId = active.id;
    const def = details.find((d: any) => d.code === CODE_COLOR_THEME_DEFAULT);
    if (def) this.colorThemeDefaultDetailId = def.id;
  }
}